/**
 * Cable Path Types
 *
 * Types for cable routing infrastructure including junction boxes and cable paths.
 */

import type { Point } from './common';
import type { Database } from './database.types';

// Database row types from generated types
export type CableJunctionRow = Database['public']['Tables']['cable_junctions']['Row'];
export type CableJunctionInsert = Database['public']['Tables']['cable_junctions']['Insert'];
export type CableJunctionUpdate = Database['public']['Tables']['cable_junctions']['Update'];

export type CablePathRow = Database['public']['Tables']['cable_paths']['Row'];
export type CablePathInsert = Database['public']['Tables']['cable_paths']['Insert'];
export type CablePathUpdate = Database['public']['Tables']['cable_paths']['Update'];

// ============================================
// Cable Types
// ============================================

/** How the cable is referenced - generic type or specific product */
export type CableRefType = 'generic' | 'product';

/**
 * Generic cable types for common cable categories.
 * Includes data, lighting control, power, and low voltage cables.
 */
export type GenericCableType =
  // Data cables
  | 'CAT5E'
  | 'CAT6'
  | 'CAT6A'
  | 'CAT7'
  | 'FIBER_SINGLE'
  | 'FIBER_MULTI'
  // Lighting control cables
  | 'DMX_3PIN'
  | 'DMX_5PIN'
  | 'CONTROL_DALI'
  | 'CONTROL_0_10V'
  | 'CONTROL_LUTRON'
  | 'CONTROL_CRESTRON'
  | 'CONTROL_LOW_VOLTAGE'
  // Power cables
  | 'POWER_12_2'
  | 'POWER_12_3'
  | 'POWER_14_2'
  | 'POWER_14_3'
  | 'POWER_10_2'
  | 'POWER_10_3'
  // Low voltage
  | 'LV_18_2'
  | 'LV_16_2'
  | 'LV_SPEAKER_14_2'
  | 'LV_SPEAKER_14_4'
  | 'LV_SPEAKER_16_2'
  | 'LV_SPEAKER_16_4'
  | 'COAX_RG6'
  | 'COAX_RG59'
  | 'HDMI'
  | 'USB'
  // Other
  | 'CUSTOM';

/** Display labels for generic cable types */
export const GENERIC_CABLE_LABELS: Record<GenericCableType, string> = {
  // Data
  CAT5E: 'Cat5e',
  CAT6: 'Cat6',
  CAT6A: 'Cat6A',
  CAT7: 'Cat7',
  FIBER_SINGLE: 'Fiber (Single Mode)',
  FIBER_MULTI: 'Fiber (Multi Mode)',
  // Lighting control
  DMX_3PIN: 'DMX 3-Pin',
  DMX_5PIN: 'DMX 5-Pin',
  CONTROL_DALI: 'DALI',
  CONTROL_0_10V: '0-10V',
  CONTROL_LUTRON: 'Lutron',
  CONTROL_CRESTRON: 'Crestron',
  CONTROL_LOW_VOLTAGE: 'Low Voltage Control',
  // Power
  POWER_12_2: '12/2 Romex',
  POWER_12_3: '12/3 Romex',
  POWER_14_2: '14/2 Romex',
  POWER_14_3: '14/3 Romex',
  POWER_10_2: '10/2 Romex',
  POWER_10_3: '10/3 Romex',
  // Low voltage
  LV_18_2: '18/2 LV',
  LV_16_2: '16/2 LV',
  LV_SPEAKER_14_2: '14/2 Speaker',
  LV_SPEAKER_14_4: '14/4 Speaker',
  LV_SPEAKER_16_2: '16/2 Speaker',
  LV_SPEAKER_16_4: '16/4 Speaker',
  COAX_RG6: 'RG6 Coax',
  COAX_RG59: 'RG59 Coax',
  HDMI: 'HDMI',
  USB: 'USB',
  // Other
  CUSTOM: 'Custom',
};

/** Cable categories for grouping in UI */
export const CABLE_CATEGORIES = {
  data: ['CAT5E', 'CAT6', 'CAT6A', 'CAT7', 'FIBER_SINGLE', 'FIBER_MULTI'] as GenericCableType[],
  lighting: [
    'DMX_3PIN',
    'DMX_5PIN',
    'CONTROL_DALI',
    'CONTROL_0_10V',
    'CONTROL_LUTRON',
    'CONTROL_CRESTRON',
    'CONTROL_LOW_VOLTAGE',
  ] as GenericCableType[],
  power: [
    'POWER_12_2',
    'POWER_12_3',
    'POWER_14_2',
    'POWER_14_3',
    'POWER_10_2',
    'POWER_10_3',
  ] as GenericCableType[],
  lowVoltage: [
    'LV_18_2',
    'LV_16_2',
    'LV_SPEAKER_14_2',
    'LV_SPEAKER_14_4',
    'LV_SPEAKER_16_2',
    'LV_SPEAKER_16_4',
    'COAX_RG6',
    'COAX_RG59',
    'HDMI',
    'USB',
  ] as GenericCableType[],
  other: ['CUSTOM'] as GenericCableType[],
};

// ============================================
// Conduit Types
// ============================================

/** Conduit trade sizes */
export type ConduitSize = '1/2' | '3/4' | '1' | '1-1/4' | '1-1/2' | '2' | '2-1/2' | '3' | '4';

/** Conduit material types */
export type ConduitMaterial = 'EMT' | 'IMC' | 'RMC' | 'PVC' | 'LFMC' | 'ENT';

/** Display labels for conduit materials */
export const CONDUIT_MATERIAL_LABELS: Record<ConduitMaterial, string> = {
  EMT: 'EMT (Electrical Metallic Tubing)',
  IMC: 'IMC (Intermediate Metal Conduit)',
  RMC: 'RMC (Rigid Metal Conduit)',
  PVC: 'PVC (Schedule 40/80)',
  LFMC: 'LFMC (Liquidtight Flexible Metal)',
  ENT: 'ENT (Electrical Nonmetallic Tubing)',
};

/** Conduit sizes in order */
export const CONDUIT_SIZES: ConduitSize[] = [
  '1/2',
  '3/4',
  '1',
  '1-1/4',
  '1-1/2',
  '2',
  '2-1/2',
  '3',
  '4',
];

/** Conduit configuration stored in JSONB */
export interface ConduitConfig {
  size: ConduitSize;
  material: ConduitMaterial;
  fill_percent?: number;
}

// ============================================
// Path Types
// ============================================

/** Path routing type */
export type PathType = 'conduit' | 'cable_only';

/** Physical route location */
export type PathRoute = 'ceiling' | 'floor' | 'wall';

/** Display labels for path types */
export const PATH_TYPE_LABELS: Record<PathType, string> = {
  conduit: 'Conduit',
  cable_only: 'Cable Only (Free Air/Tray)',
};

/** Display labels for routes */
export const PATH_ROUTE_LABELS: Record<PathRoute, string> = {
  ceiling: 'Ceiling',
  floor: 'Floor',
  wall: 'Wall',
};

// ============================================
// Cable Entry (stored in cables JSONB array)
// ============================================

/** A single cable entry in a path's cables array */
export interface CableEntry {
  /** How the cable is identified */
  cable_ref_type: CableRefType;
  /** Generic cable type (when cable_ref_type = 'generic') */
  generic_type?: GenericCableType;
  /** Product library ID (when cable_ref_type = 'product') */
  product_id?: string | null;
  /** Quantity of this cable */
  qty: number;
  /** Extra length at start/junction A (service loop, etc.) */
  extra_end_a: number;
  /** Extra length at end/junction B */
  extra_end_b: number;
  /** Unit for extra lengths (matches path's length_unit) */
  extra_unit: string;
}

/** Default cable entry for new cables */
export const DEFAULT_CABLE_ENTRY: CableEntry = {
  cable_ref_type: 'generic',
  generic_type: 'CAT6',
  product_id: null,
  qty: 1,
  extra_end_a: 0,
  extra_end_b: 0,
  extra_unit: 'ft',
};

// ============================================
// Domain Entities
// ============================================

/** Purpose type for junction labels */
export type JunctionPurposeType = 'from' | 'to';

/**
 * Cable Junction entity with typed fields.
 * Junction boxes are bifurcation points where multiple cable paths can connect.
 */
export interface CableJunction {
  id: string;
  floor_plan_id: string;
  name: string;
  x: number;
  y: number;
  product_id: string | null;
  notes: string | null;
  // Scale factor for icon size (1.0 = 100%)
  scale: number;
  // Purpose label fields (for indicating "FROM: X" or "TO: Y")
  purpose_type: JunctionPurposeType | null;
  purpose_label: string | null;
  label_offset_x: number;
  label_offset_y: number;
  label_width: number;
  label_font_size: number;
  // Timestamps
  created_at: string;
  updated_at: string;
  created_by_clerk_id: string | null;
}

/**
 * Cable Path entity with typed fields.
 * Paths represent cable runs between points/junctions.
 */
export interface CablePath {
  id: string;
  floor_plan_id: string;
  name: string | null;
  start_junction_id: string | null;
  end_junction_id: string | null;
  /** Intermediate vertices only - start/end derived from junctions */
  points: Point[];
  length_real: number | null;
  length_unit: string;
  path_type: PathType;
  route: PathRoute;
  conduit: ConduitConfig | null;
  cables: CableEntry[];
  vertical_drop_start: number;
  vertical_drop_end: number;
  vertical_drop_unit: string;
  created_at: string;
  updated_at: string;
  created_by_clerk_id: string | null;
}

// ============================================
// Create/Update Payloads
// ============================================

export type CreateCableJunction = Omit<CableJunction, 'id' | 'created_at' | 'updated_at'>;
export type UpdateCableJunction = Partial<
  Omit<CableJunction, 'id' | 'floor_plan_id' | 'created_at' | 'updated_at'>
>;

export type CreateCablePath = Omit<CablePath, 'id' | 'created_at' | 'updated_at'>;
export type UpdateCablePath = Partial<
  Omit<CablePath, 'id' | 'floor_plan_id' | 'created_at' | 'updated_at'>
>;

// ============================================
// Drawing State
// ============================================

/** State for cable path drawing tool */
export interface CablePathDrawingState {
  /** Whether actively drawing a path */
  isDrawing: boolean;
  /** Current vertices being drawn */
  currentPoints: Point[];
  /** Junction ID if path started from a junction */
  startJunctionId: string | null;
  /** Junction being snapped to (for end point) */
  snapToJunctionId: string | null;
  /** Real-time length in current unit */
  liveLength: number;
}

/** Initial drawing state */
export const INITIAL_DRAWING_STATE: CablePathDrawingState = {
  isDrawing: false,
  currentPoints: [],
  startJunctionId: null,
  snapToJunctionId: null,
  liveLength: 0,
};

// ============================================
// Reporting / Aggregation
// ============================================

/** Aggregated cable requirement for reporting */
export interface CableRequirement {
  /** Cable identifier (product_id or generic_type) */
  identifier: string;
  /** Display label */
  label: string;
  /** Whether this is a product or generic cable */
  type: CableRefType;
  /** Product ID if type is 'product' */
  product_id?: string | null;
  /** Generic type if type is 'generic' */
  generic_type?: GenericCableType;
  /** Total length required */
  total_length: number;
  /** Unit for length */
  unit: string;
  /** Number of paths using this cable */
  path_count: number;
}

// ============================================
// Utility Functions
// ============================================

/**
 * Parse a CableJunctionRow from the database into a typed CableJunction.
 */
export function parseCableJunction(row: CableJunctionRow): CableJunction {
  return {
    id: row.id,
    floor_plan_id: row.floor_plan_id,
    name: row.name,
    x: row.x,
    y: row.y,
    product_id: row.product_id,
    notes: row.notes,
    // Scale factor
    scale: (row as any).scale ?? 1.0,
    // Purpose label fields
    purpose_type: (row as any).purpose_type as JunctionPurposeType | null,
    purpose_label: (row as any).purpose_label ?? null,
    label_offset_x: (row as any).label_offset_x ?? 25,
    label_offset_y: (row as any).label_offset_y ?? -15,
    label_width: (row as any).label_width ?? 120,
    label_font_size: (row as any).label_font_size ?? 10,
    // Timestamps
    created_at: row.created_at ?? new Date().toISOString(),
    updated_at: row.updated_at ?? new Date().toISOString(),
    created_by_clerk_id: row.created_by_clerk_id,
  };
}

/**
 * Parse a CablePathRow from the database into a typed CablePath.
 */
export function parseCablePath(row: CablePathRow): CablePath {
  return {
    id: row.id,
    floor_plan_id: row.floor_plan_id,
    name: row.name,
    start_junction_id: row.start_junction_id,
    end_junction_id: row.end_junction_id,
    points: (row.points as Point[]) || [],
    length_real: row.length_real,
    length_unit: row.length_unit ?? 'ft',
    path_type: (row.path_type as PathType) ?? 'conduit',
    route: (row.route as PathRoute) ?? 'ceiling',
    conduit: row.conduit as ConduitConfig | null,
    cables: (row.cables as CableEntry[]) || [],
    vertical_drop_start: row.vertical_drop_start ?? 10,
    vertical_drop_end: row.vertical_drop_end ?? 10,
    vertical_drop_unit: row.vertical_drop_unit ?? 'ft',
    created_at: row.created_at ?? new Date().toISOString(),
    updated_at: row.updated_at ?? new Date().toISOString(),
    created_by_clerk_id: row.created_by_clerk_id,
  };
}

/**
 * Get the display label for a cable entry.
 */
export function getCableLabel(entry: CableEntry): string {
  if (entry.cable_ref_type === 'product' && entry.product_id) {
    return `Product: ${entry.product_id.slice(0, 8)}...`;
  }
  return entry.generic_type ? GENERIC_CABLE_LABELS[entry.generic_type] : 'Unknown Cable';
}

/**
 * Calculate total cable length including extras and quantity.
 */
export function calculateCableTotalLength(pathLength: number, entry: CableEntry): number {
  const baseLength = pathLength + entry.extra_end_a + entry.extra_end_b;
  return baseLength * entry.qty;
}
